import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ShoppingBag, Plus, Trash2, Pencil, Save, X, Eye, EyeOff,
  Loader2, RefreshCw, ClipboardList, Clock, MapPin, Home, Phone,
  CheckCircle2, CircleDot, Package, Upload, Image as ImageIcon,
  Recycle, AlertTriangle, Receipt,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function client() {
  const tok = localStorage.getItem("admin_token");
  return axios.create({ baseURL: API, headers: tok ? { Authorization: `Bearer ${tok}` } : {} });
}
const eur = (n) => `€ ${Number(n || 0).toFixed(2)}`;

/**
 * Admin panel for the takeaway module.
 * Two sub-tabs: Menu (CRUD dei piatti) + Ordini (lista + cambio stato).
 */
export default function TakeawayEditor() {
  const [sub, setSub] = useState("orders");
  return (
    <div className="space-y-4" data-testid="takeaway-editor">
      <header className="rounded-2xl bg-gradient-to-br from-[#2b1410] to-[#4a1c17] text-[#f7ebdf] p-5 flex flex-wrap items-center gap-4">
        <div className="h-11 w-11 rounded-lg bg-white/10 flex items-center justify-center">
          <ShoppingBag className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-serif text-2xl font-bold">Asporto</h2>
          <p className="text-sm text-[#f7ebdf]/75 mt-0.5">
            Cena 19:00-22:00 · consegna 10 km (5€ · gratis sopra 50€ · min 25€) · pagamento contanti o online
          </p>
        </div>
        <LaunchSwitch />
      </header>

      <nav className="flex gap-1 border-b border-[#e8dcc8]">
        <SubTab active={sub === "orders"} onClick={() => setSub("orders")} icon={<ClipboardList className="h-4 w-4" />}>Ordini</SubTab>
        <SubTab active={sub === "menu"} onClick={() => setSub("menu")} icon={<Package className="h-4 w-4" />}>Menu</SubTab>
        <SubTab active={sub === "packaging"} onClick={() => setSub("packaging")} icon={<Recycle className="h-4 w-4" />}>Confezioni</SubTab>
      </nav>

      {sub === "orders" ? <OrdersPanel /> : sub === "menu" ? <MenuPanel /> : <PackagingPanel />}
    </div>
  );
}

// Launch gate: while ON, /asporto shows the "coming soon" screen and orders are refused server-side.
function LaunchSwitch() {
  const [st, setSt] = useState(null);
  const [text, setText] = useState("");
  const [editing, setEditing] = useState(false);
  const hdr = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("admin_token") || ""}` } });
  useEffect(() => { axios.get(`${API}/takeaway/status`).then((r) => { setSt(r.data); setText(r.data.opening_text || ""); }).catch(() => {}); }, []);
  const save = async (patch) => {
    try { const r = await axios.put(`${API}/takeaway/admin/status`, patch, hdr()); setSt(r.data); toast.success(r.data.coming_soon ? "Asporto in attesa: pagina 'prossimamente' attiva" : "Asporto PUBBLICATO: ordini aperti"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  if (!st) return null;
  return (
    <div className="ml-auto flex flex-col items-end gap-2 text-right" data-testid="takeaway-launch">
      <button onClick={() => { if (st.coming_soon && !window.confirm("Pubblicare l'Asporto? La pagina diventa ordinabile da tutti.")) return; save({ coming_soon: !st.coming_soon }); }}
        className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold border ${st.coming_soon ? "bg-amber-400 text-[#2b1410] border-amber-400" : "bg-emerald-500 text-white border-emerald-500"}`} data-testid="takeaway-launch-toggle">
        <span className={`h-2 w-2 rounded-full ${st.coming_soon ? "bg-[#2b1410]" : "bg-white animate-pulse"}`} /> {st.coming_soon ? "In attesa · prossimamente" : "Pubblicato · ordini aperti"}
      </button>
      <div className="flex items-center gap-2 text-[11px]">
        <a href="/asporto?preview=1" target="_blank" rel="noreferrer" className="underline text-[#f7ebdf]/80" data-testid="takeaway-preview-link">Anteprima admin</a>
        <a href="/asporto" target="_blank" rel="noreferrer" className="underline text-[#f7ebdf]/80" data-testid="takeaway-public-link">Pagina pubblica</a>
        <button onClick={() => setEditing((e) => !e)} className="underline text-[#f7ebdf]/80" data-testid="takeaway-launch-text-btn">Testo attesa</button>
      </div>
      {editing && (
        <div className="flex gap-2 w-full sm:w-[420px]">
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Testo mostrato nella pagina di attesa (vuoto = predefinito)" className="flex-1 rounded-lg px-3 py-1.5 text-xs text-[#2b1410]" data-testid="takeaway-launch-text" />
          <button onClick={() => { save({ opening_text: text }); setEditing(false); }} className="rounded-lg bg-[#C48B31] text-white px-3 py-1.5 text-xs font-bold" data-testid="takeaway-launch-text-save">Salva</button>
        </div>
      )}
    </div>
  );
}

function SubTab({ active, onClick, icon, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 transition
        ${active ? "border-[#4a1c17] text-[#4a1c17]" : "border-transparent text-[#7c5a4e] hover:text-[#2b1410]"}`}
    >
      {icon}
      {children}
    </button>
  );
}

// ---------------------------- Orders panel ----------------------------

const STATUS_FLOW = [
  { key: "pending", label: "In attesa", cls: "bg-amber-100 text-amber-800" },
  { key: "confirmed", label: "Confermato", cls: "bg-blue-100 text-blue-800" },
  { key: "ready", label: "Pronto", cls: "bg-emerald-100 text-emerald-800" },
  { key: "delivered", label: "Consegnato", cls: "bg-[#4a1c17] text-[#f7ebdf]" },
  { key: "cancelled", label: "Annullato", cls: "bg-red-100 text-red-800" },
];
const nextStatus = (s) => ({ pending: "confirmed", confirmed: "ready", ready: "delivered" }[s]);

function OrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client().get(`/takeaway/admin/orders${filter ? `?status=${filter}` : ""}`);
      setOrders(data || []);
    } catch (e) {
      toast.error("Errore caricamento ordini");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [filter]);

  const setStatus = async (id, status) => {
    try {
      await client().patch(`/takeaway/admin/orders/${id}`, { status });
      load();
    } catch (e) {
      toast.error("Errore aggiornamento");
    }
  };

  const markPaid = async (id) => {
    try {
      await client().patch(`/takeaway/admin/orders/${id}`, { payment_status: "paid" });
      toast.success("Ordine segnato come pagato");
      load();
    } catch {
      toast.error("Errore aggiornamento pagamento");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setFilter("")}
          className={`rounded-full border px-3 py-1 text-xs ${!filter ? "bg-[#4a1c17] text-[#f7ebdf] border-[#4a1c17]" : "border-[#e8dcc8] bg-white"}`}
        >Tutti</button>
        {STATUS_FLOW.map((s) => (
          <button
            key={s.key}
            onClick={() => setFilter(s.key)}
            className={`rounded-full border px-3 py-1 text-xs ${filter === s.key ? "bg-[#4a1c17] text-[#f7ebdf] border-[#4a1c17]" : "border-[#e8dcc8] bg-white"}`}
          >{s.label}</button>
        ))}
        <button onClick={load} className="ml-auto rounded-full border border-[#e8dcc8] px-3 py-1 text-xs inline-flex items-center gap-1">
          <RefreshCw className="h-3.5 w-3.5" /> Aggiorna
        </button>
      </div>

      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#4a1c17]" /></div>
      ) : orders.length === 0 ? (
        <div className="rounded-xl bg-white border border-[#e8dcc8] p-10 text-center text-sm text-[#7c5a4e]">
          Nessun ordine {filter && "per questo filtro"}.
        </div>
      ) : (
        <div className="space-y-2">
          {orders.map((o) => (
            <OrderCard key={o.id} order={o} onNext={() => setStatus(o.id, nextStatus(o.status))} onCancel={() => setStatus(o.id, "cancelled")} onMarkPaid={() => markPaid(o.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function OrderCard({ order, onNext, onCancel, onMarkPaid }) {
  const [open, setOpen] = useState(false);
  const st = STATUS_FLOW.find((s) => s.key === order.status) || STATUS_FLOW[0];
  const nxt = nextStatus(order.status);
  return (
    <div className="rounded-xl bg-white border border-[#e8dcc8]" data-testid={`takeaway-order-${order.id}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full px-4 py-3 flex items-center gap-3 text-left">
        <div className="text-[10px] font-mono text-[#7c5a4e] w-16">#{order.id.slice(0, 8)}</div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-[#2b1410] truncate flex items-center gap-2">
            {order.name}
            {order.mode === "delivery" ? <MapPin className="h-3.5 w-3.5 text-[#7c5a4e]" /> : <Home className="h-3.5 w-3.5 text-[#7c5a4e]" />}
          </div>
          <div className="text-xs text-[#7c5a4e] flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {order.date} · {order.slot}</span>
            <span>·</span>
            <span>{order.items.length} piatti · {eur(order.total)}</span>
          </div>
        </div>
        <span className={`text-[10px] uppercase font-semibold rounded-full px-2 py-0.5 ${st.cls}`}>{st.label}</span>
        {order.payment_status === "paid" && <CheckCircle2 className="h-4 w-4 text-emerald-600" title="Pagato online" />}
      </button>

      {open && (
        <div className="border-t border-[#e8dcc8] px-4 py-3 space-y-2 text-sm">
          <Row label="Telefono" value={<a href={`tel:${order.phone}`} className="text-[#4a1c17] underline">{order.phone}</a>} />
          {order.email && <Row label="Email" value={order.email} />}
          {order.mode === "delivery" && <Row label="Indirizzo" value={order.address} />}
          <Row label="Pagamento" value={order.payment_status === "paid" ? "Pagato online ✓" : "Al ritiro (contanti/POS)"} />
          {order.notes && <Row label="Note" value={order.notes} />}

          <div className="mt-2 rounded-lg bg-[#faf3ee] border border-[#e8dcc8] p-3">
            <div className="text-[11px] uppercase font-semibold text-[#7c5a4e] mb-1">Piatti</div>
            <ul className="text-sm space-y-0.5">
              {order.items.map((it, i) => (
                <li key={i} className="flex justify-between">
                  <span>{it.qty}× {it.name}{it.notes && <span className="text-[#7c5a4e] text-xs ml-1">— {it.notes}</span>}</span>
                  <span className="text-[#7c5a4e]">{eur(it.line_total)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-2 pt-2 border-t border-[#e8dcc8] flex justify-between text-xs">
              <span>Subtotale {eur(order.subtotal)}{order.delivery_fee > 0 && ` · Consegna ${eur(order.delivery_fee)}`}</span>
              <span className="font-bold text-[#2b1410]">{eur(order.total)}</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            {nxt && (
              <button
                type="button"
                onClick={onNext}
                className="rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-3 py-1 text-xs font-semibold inline-flex items-center gap-1"
              >
                <CircleDot className="h-3 w-3" /> Segna come {STATUS_FLOW.find((s) => s.key === nxt).label}
              </button>
            )}
            {order.payment_status !== "paid" && ["cash", "pos"].includes(order.payment_method) && (
              <button
                type="button"
                onClick={onMarkPaid}
                data-testid={`takeaway-mark-paid-${order.id}`}
                className="rounded-full border border-emerald-500 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 px-3 py-1 text-xs font-semibold inline-flex items-center gap-1"
              >
                <CheckCircle2 className="h-3 w-3" /> Segna come pagato
              </button>
            )}
            {!["cancelled", "delivered"].includes(order.status) && (
              <button
                type="button"
                onClick={onCancel}
                className="rounded-full border border-red-300 text-red-700 hover:bg-red-50 px-3 py-1 text-xs font-semibold"
              >Annulla ordine</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ----------------------------- Menu panel -----------------------------

function MenuPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | id

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client().get("/takeaway/admin/items");
      setItems(data || []);
    } catch { /* silent */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Eliminare questo piatto?")) return;
    try {
      await client().delete(`/takeaway/admin/items/${id}`);
      load();
    } catch { toast.error("Errore eliminazione"); }
  };

  const toggle = async (it) => {
    try {
      await client().put(`/takeaway/admin/items/${it.id}`, { ...it, active: !it.active });
      load();
    } catch { toast.error("Errore aggiornamento"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setEditing("new")}
          data-testid="takeaway-add-item"
          className="rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1"
        >
          <Plus className="h-3.5 w-3.5" /> Aggiungi piatto
        </button>
        <button onClick={load} className="rounded-full border border-[#e8dcc8] px-3 py-1.5 text-xs inline-flex items-center gap-1">
          <RefreshCw className="h-3.5 w-3.5" /> Aggiorna
        </button>
      </div>

      {editing === "new" && (
        <ItemForm onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}

      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#4a1c17]" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl bg-white border border-[#e8dcc8] p-10 text-center text-sm text-[#7c5a4e]">
          Nessun piatto — aggiungi il primo con il bottone qui sopra.
        </div>
      ) : (
        <div className="space-y-1.5">
          {items.map((it) => editing === it.id ? (
            <ItemForm key={it.id} item={it} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
          ) : (
            <div key={it.id} className="rounded-xl bg-white border border-[#e8dcc8] p-3 flex items-center gap-3">
              {it.image ? (
                <img src={it.image} alt="" className="h-12 w-12 rounded-lg object-cover" />
              ) : (
                <div className="h-12 w-12 rounded-lg bg-[#faf3ee] border border-[#e8dcc8] grid place-items-center text-[10px] text-[#7c5a4e]">FOTO</div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-[#7c5a4e]">{it.category}</div>
                <div className="font-semibold text-[#2b1410] truncate">{it.name}</div>
                {it.description && <div className="text-xs text-[#7c5a4e] truncate">{it.description}</div>}
              </div>
              <div className="text-sm font-bold text-[#4a1c17] whitespace-nowrap">{eur(it.price)}</div>
              <button
                type="button"
                onClick={() => toggle(it)}
                className={`p-1.5 rounded-full ${it.active ? "text-emerald-700" : "text-[#7c5a4e]"} hover:bg-[#f7ebdf]`}
                title={it.active ? "Visibile · nascondi" : "Nascosto · rendi visibile"}
                data-testid={`takeaway-toggle-${it.id}`}
              >
                {it.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </button>
              <button type="button" onClick={() => setEditing(it.id)} className="p-1.5 rounded-full hover:bg-[#f7ebdf]" title="Modifica">
                <Pencil className="h-4 w-4 text-[#4a1c17]" />
              </button>
              <button type="button" onClick={() => remove(it.id)} className="p-1.5 rounded-full hover:bg-red-50" title="Elimina">
                <Trash2 className="h-4 w-4 text-red-600" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ItemForm({ item, onCancel, onSaved }) {
  const [f, setF] = useState(item || { category: "", name: "", description: "", price: "", image: "", active: true, sort_order: 0, badges: [], is_featured: false, included_ingredients: [], extras: [] });
  const [saving, setSaving] = useState(false);
  // Local buffers for the two chip inputs — press Enter (or comma) to commit.
  const [inclInput, setInclInput] = useState("");
  const [extraName, setExtraName] = useState("");
  const [extraPrice, setExtraPrice] = useState("");
  const isEdit = !!item;
  const on = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const toggleBadge = (b) => setF((s) => {
    const list = Array.isArray(s.badges) ? s.badges : [];
    return { ...s, badges: list.includes(b) ? list.filter((x) => x !== b) : [...list, b] };
  });

  // ----- Ingredient chip helpers -----
  const addIncluded = () => {
    const v = inclInput.trim().replace(/,+$/, "");
    if (!v) return;
    setF((s) => {
      const cur = Array.isArray(s.included_ingredients) ? s.included_ingredients : [];
      if (cur.includes(v)) return s;
      return { ...s, included_ingredients: [...cur, v] };
    });
    setInclInput("");
  };
  const removeIncluded = (name) => setF((s) => ({
    ...s,
    included_ingredients: (s.included_ingredients || []).filter((n) => n !== name),
  }));
  const addExtra = () => {
    const n = extraName.trim();
    const p = Number(extraPrice);
    if (!n || !(p >= 0)) return;
    setF((s) => {
      const cur = Array.isArray(s.extras) ? s.extras : [];
      if (cur.some((x) => x.name === n)) return s;
      return { ...s, extras: [...cur, { name: n, price: p }] };
    });
    setExtraName(""); setExtraPrice("");
  };
  const removeExtra = (name) => setF((s) => ({
    ...s,
    extras: (s.extras || []).filter((x) => x.name !== name),
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.category?.trim() || !f.name?.trim() || !f.price) {
      toast.error("Categoria, nome e prezzo obbligatori"); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...f,
        price: Number(f.price),
        sort_order: Number(f.sort_order) || 0,
        badges: Array.isArray(f.badges) ? f.badges : [],
        is_featured: !!f.is_featured,
        included_ingredients: Array.isArray(f.included_ingredients) ? f.included_ingredients : [],
        extras: Array.isArray(f.extras)
          ? f.extras.map((x) => ({ name: String(x.name || "").trim(), price: Number(x.price) || 0 })).filter((x) => x.name)
          : [],
      };
      if (isEdit) await client().put(`/takeaway/admin/items/${item.id}`, payload);
      else await client().post("/takeaway/admin/items", payload);
      toast.success(isEdit ? "Piatto salvato" : "Piatto aggiunto");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const BADGES = [
    { key: "veg", label: "Veg" },
    { key: "spicy", label: "Piccante" },
    { key: "signature", label: "Signature" },
    { key: "new", label: "Novità" },
    { key: "senza-glutine", label: "Senza glutine" },
  ];

  return (
    <form onSubmit={submit} className="rounded-xl bg-[#fbf6ef] border border-[#e8dcc8] p-4 grid sm:grid-cols-2 gap-3" data-testid="takeaway-item-form">
      <Field label="Categoria *"><input required value={f.category} onChange={on("category")} placeholder="Hamburger / Pizza / Fritti" className="tk-input" /></Field>
      <Field label="Prezzo € *"><input required type="number" step="0.5" value={f.price} onChange={on("price")} className="tk-input" /></Field>
      <Field label="Nome *"><input required value={f.name} onChange={on("name")} className="tk-input" /></Field>
      <Field label="Ordine (opzionale)"><input type="number" value={f.sort_order} onChange={on("sort_order")} className="tk-input" /></Field>
      <Field label="Descrizione" className="sm:col-span-2"><input value={f.description} onChange={on("description")} className="tk-input" /></Field>
      <Field label="Foto piatto" className="sm:col-span-2">
        <ImageUploader value={f.image} onChange={(url) => setF((v) => ({ ...v, image: url }))} />
      </Field>

      <Field label="Badge (icone che appaiono sulla card)" className="sm:col-span-2">
        <div className="flex flex-wrap gap-2 mt-1">
          {BADGES.map((b) => {
            const active = Array.isArray(f.badges) && f.badges.includes(b.key);
            return (
              <button
                key={b.key}
                type="button"
                onClick={() => toggleBadge(b.key)}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${active ? "bg-[#4a1c17] text-[#f7ebdf] border-[#4a1c17]" : "bg-white text-[#4a1c17] border-[#e8dcc8] hover:bg-[#faf3ee]"}`}
                data-testid={`takeaway-badge-${b.key}`}
              >
                {b.label}
              </button>
            );
          })}
        </div>
      </Field>

      <Field label="In evidenza" className="sm:col-span-2">
        <label className="inline-flex items-center gap-2 text-sm text-[#2b1410]">
          <input
            type="checkbox"
            checked={!!f.is_featured}
            onChange={(e) => setF((v) => ({ ...v, is_featured: e.target.checked }))}
            data-testid="takeaway-featured-toggle"
            className="h-4 w-4 accent-[#4a1c17]"
          />
          Mostra nella sezione “Consigliati dallo chef”
        </label>
      </Field>

      {/* Ingredienti compresi — chip list. Il cliente li vede spuntati e può
          toglierli (senza cambio prezzo). Es. hamburger: lattuga, pomodoro, bacon. */}
      <Field label="Ingredienti compresi (il cliente può toglierli)" className="sm:col-span-2">
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid="takeaway-included-list">
          {(f.included_ingredients || []).map((n) => (
            <span key={n} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 pl-3 pr-1 py-0.5 text-xs">
              {n}
              <button
                type="button"
                onClick={() => removeIncluded(n)}
                className="rounded-full hover:bg-emerald-200/60 p-0.5"
                aria-label={`Rimuovi ${n}`}
                data-testid={`takeaway-included-remove-${n}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          {(!f.included_ingredients || f.included_ingredients.length === 0) && (
            <span className="text-xs text-[#7c5a4e] italic">Nessun ingrediente aggiunto</span>
          )}
        </div>
        <div className="flex gap-2">
          <input
            value={inclInput}
            onChange={(e) => setInclInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addIncluded(); }
            }}
            onBlur={addIncluded}
            placeholder="es. lattuga, pomodoro, bacon…"
            className="tk-input flex-1"
            data-testid="takeaway-included-input"
          />
          <button
            type="button"
            onClick={addIncluded}
            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1"
            data-testid="takeaway-included-add"
          >
            <Plus className="h-3.5 w-3.5" /> Aggiungi
          </button>
        </div>
        <p className="mt-1 text-[11px] text-[#7c5a4e]">Suggerimento: premi Invio o virgola per aggiungere velocemente più ingredienti.</p>
      </Field>

      {/* Extra a pagamento — coppia nome + prezzo. Es. tartufo €4, doppia mozzarella €2. */}
      <Field label="Extra a pagamento (il cliente li aggiunge se vuole)" className="sm:col-span-2">
        <div className="space-y-1 mb-2" data-testid="takeaway-extras-list">
          {(f.extras || []).map((x) => (
            <div key={x.name} className="flex items-center justify-between gap-2 rounded-lg bg-[#c48b31]/10 border border-[#c48b31]/30 px-3 py-1.5 text-xs">
              <span className="text-[#2b1410] font-medium">{x.name}</span>
              <span className="text-[#4a1c17] font-bold">+ € {Number(x.price).toFixed(2)}</span>
              <button
                type="button"
                onClick={() => removeExtra(x.name)}
                className="rounded-full hover:bg-[#c48b31]/20 p-1"
                aria-label={`Rimuovi ${x.name}`}
                data-testid={`takeaway-extra-remove-${x.name}`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          {(!f.extras || f.extras.length === 0) && (
            <span className="text-xs text-[#7c5a4e] italic">Nessun extra aggiunto</span>
          )}
        </div>
        <div className="grid grid-cols-[1fr_100px_auto] gap-2">
          <input
            value={extraName}
            onChange={(e) => setExtraName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addExtra(); } }}
            placeholder="es. tartufo"
            className="tk-input"
            data-testid="takeaway-extra-name-input"
          />
          <input
            type="number"
            step="0.5"
            min="0"
            value={extraPrice}
            onChange={(e) => setExtraPrice(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addExtra(); } }}
            placeholder="€ 4"
            className="tk-input"
            data-testid="takeaway-extra-price-input"
          />
          <button
            type="button"
            onClick={addExtra}
            className="rounded-full bg-[#c48b31] hover:bg-[#a87422] text-white px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1"
            data-testid="takeaway-extra-add"
          >
            <Plus className="h-3.5 w-3.5" /> Aggiungi
          </button>
        </div>
      </Field>

      <div className="sm:col-span-2 flex items-center gap-2 justify-end">
        <button type="button" onClick={onCancel} className="rounded-full border border-[#e8dcc8] bg-white px-3 py-1.5 text-xs inline-flex items-center gap-1">
          <X className="h-3.5 w-3.5" /> Annulla
        </button>
        <button type="submit" disabled={saving} className="rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-4 py-1.5 text-xs font-semibold inline-flex items-center gap-1">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Salva
        </button>
      </div>
    </form>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block text-[11px] uppercase tracking-wide font-semibold text-[#7c5a4e] ${className}`}>
      {label}
      <div className="mt-1">{children}</div>
    </label>
  );
}

function ImageUploader({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef(null);
  const onFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Seleziona un'immagine"); return; }
    if (file.size > 3 * 1024 * 1024) { toast.error("Immagine troppo grande (max 3MB). Prova a comprimerla."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await client().post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = data?.url;
      if (!url) throw new Error("URL non ricevuto dal server");
      onChange(url);
      toast.success("Foto caricata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Upload fallito");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  return (
    <div className="flex items-center gap-3">
      {value ? (
        <div className="relative">
          <img src={value} alt="" className="h-20 w-20 rounded-lg object-cover border border-[#e8dcc8]" />
          <button
            type="button"
            onClick={() => onChange("")}
            data-testid="tk-image-remove"
            className="absolute -top-2 -right-2 rounded-full bg-white shadow border border-[#e8dcc8] p-1 hover:bg-red-50"
            title="Rimuovi foto"
          >
            <X className="h-3 w-3 text-red-600" />
          </button>
        </div>
      ) : (
        <div className="h-20 w-20 rounded-lg border-2 border-dashed border-[#e8dcc8] flex items-center justify-center text-[#c8b8a6]">
          <ImageIcon className="h-6 w-6" />
        </div>
      )}
      <div className="flex-1 space-y-1.5">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => onFile(e.target.files?.[0])}
          data-testid="tk-image-input"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="tk-image-upload"
          className="inline-flex items-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-4 py-1.5 text-xs font-semibold disabled:opacity-60"
        >
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          {value ? "Cambia foto" : "Carica foto"}
        </button>
        <p className="text-[10px] text-[#7c5a4e] normal-case tracking-normal">
          JPG o PNG · max 3 MB · consigliato quadrato 800×800
        </p>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-2 text-sm">
      <div className="text-[11px] uppercase tracking-wide text-[#7c5a4e] pt-0.5">{label}</div>
      <div className="text-[#2b1410]">{value}</div>
    </div>
  );
}

// Inject a tiny style so tk-input works without a Tailwind class conflict.
const _tkstyle = `
  .tk-input { display:block; width:100%; padding:.5rem .75rem; border-radius:.375rem; border:1px solid #e8dcc8; background:white; font-size:.875rem; outline:none; }
  .tk-input:focus { box-shadow:0 0 0 2px rgba(74,28,23,.25); border-color:#4a1c17; }
`;
if (typeof document !== "undefined" && !document.getElementById("takeaway-admin-style")) {
  const s = document.createElement("style"); s.id = "takeaway-admin-style"; s.textContent = _tkstyle; document.head.appendChild(s);
}

/**
 * PackagingPanel — third sub-tab of the takeaway admin. Lists customers
 * who chose "keep_reuse" and either still have time (pending) or lapsed
 * past the grace period (expired). Expired records ship with a
 * ready-to-print receipt; admin can mark each as paid or returned.
 */
function PackagingPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState({}); // { orderId: bool }
  const load = () => client().get("/takeaway/admin/packaging-expirations").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  const settle = async (orderId, action) => {
    setBusy((b) => ({ ...b, [orderId]: true }));
    try {
      await client().post(`/takeaway/admin/packaging-expirations/${orderId}/settle`, { action });
      toast.success(action === "paid" ? "Marcato come pagato" : "Marcato come restituito");
      await load();
    } catch (e) {
      toast.error("Errore: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy((b) => ({ ...b, [orderId]: false }));
    }
  };
  if (!data) return <div className="p-6 text-center text-[#7c5a4e]"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Caricamento…</div>;
  return (
    <div className="space-y-6" data-testid="packaging-panel">
      <div className="rounded-xl bg-white border border-[#e8dcc8] p-4 flex flex-wrap gap-4 text-sm">
        <div><span className="text-[#7c5a4e]">Promessi al cliente:</span> <strong>{data.promised_days} gg</strong></div>
        <div><span className="text-[#7c5a4e]">Grace effettivo:</span> <strong>{data.grace_days} gg</strong></div>
        <button onClick={load} className="ml-auto text-xs text-[#4a1c17] hover:underline inline-flex items-center gap-1"><RefreshCw className="h-3 w-3" /> Aggiorna</button>
      </div>

      <PackagingSection
        title="Scaduti — da incassare o segnare come restituiti"
        icon={<AlertTriangle className="h-4 w-4 text-red-700" />}
        rows={data.expired} empty="Nessuna scadenza pendente." expired
        busy={busy} onSettle={settle} />
      <PackagingSection
        title="In scadenza — ancora entro il periodo di grace"
        icon={<Clock className="h-4 w-4 text-[#c48b31]" />}
        rows={data.pending} empty="Nessun ordine in attesa di restituzione."
        busy={busy} onSettle={settle} />
    </div>
  );
}

function PackagingSection({ title, icon, rows, empty, expired, busy, onSettle }) {
  return (
    <section>
      <h3 className="font-semibold text-[#2b1410] flex items-center gap-2 mb-2">{icon} {title} <span className="text-xs text-[#7c5a4e]">({rows.length})</span></h3>
      {rows.length === 0 ? (
        <div className="rounded-xl bg-[#faf3ee]/70 border border-dashed border-[#e8dcc8] p-4 text-sm text-[#7c5a4e]">{empty}</div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.order_id} className={`rounded-xl border p-3 ${expired ? "bg-red-50/40 border-red-200" : "bg-white border-[#e8dcc8]"}`} data-testid={`pkg-row-${r.order_id}`}>
              <div className="flex flex-wrap items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-[#2b1410]">{r.customer_name}</div>
                  <div className="text-xs text-[#7c5a4e] flex flex-wrap gap-x-3">
                    <span><Phone className="inline h-3 w-3 mr-0.5" />{r.customer_phone}</span>
                    {r.customer_address && <span><MapPin className="inline h-3 w-3 mr-0.5" />{r.customer_address}</span>}
                  </div>
                  <div className="text-[11px] text-[#7c5a4e] mt-0.5">
                    Ordine: {new Date(r.order_date).toLocaleDateString("it-IT")} · {r.age_days} giorni fa
                    {!expired && r.days_left > 0 && <span> · <strong className="text-[#c48b31]">restano {r.days_left} gg</strong></span>}
                    {expired && <span> · <strong className="text-red-700">scaduto da {Math.abs(r.days_left)} gg</strong></span>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[11px] uppercase tracking-wider text-[#7c5a4e]">Da incassare</div>
                  <div className="font-bold text-lg text-[#2b1410]">{eur(r.receipt.total)}</div>
                </div>
              </div>
              <div className="mt-2 rounded-lg bg-[#faf3ee] border border-[#e8dcc8] p-2.5 text-xs">
                <div className="text-[10px] uppercase tracking-wider text-[#7c5a4e] mb-1 flex items-center gap-1"><Receipt className="h-3 w-3" /> Ricevuta</div>
                {r.receipt.lines.length === 0 ? (
                  <div className="italic text-[#7c5a4e]">Nessuna confezione tracciata su questo ordine — imposta manualmente le unità se necessario.</div>
                ) : (
                  <ul className="divide-y divide-[#e8dcc8]/60">
                    {r.receipt.lines.map((l, i) => (
                      <li key={i} className="py-1 flex justify-between"><span>{l.qty}× {l.label}</span><span>{eur(l.line_total)}</span></li>
                    ))}
                  </ul>
                )}
                <div className="mt-1.5 text-[10px] text-[#7c5a4e] italic">{r.receipt.note}</div>
              </div>
              {expired && (
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => onSettle(r.order_id, "paid")}
                    disabled={busy[r.order_id]}
                    data-testid={`pkg-mark-paid-${r.order_id}`}
                    className="rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-3 py-1.5 text-xs font-semibold disabled:opacity-50 inline-flex items-center gap-1"
                  >
                    {busy[r.order_id] ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                    Marca pagato
                  </button>
                  <button
                    onClick={() => onSettle(r.order_id, "returned")}
                    disabled={busy[r.order_id]}
                    data-testid={`pkg-mark-returned-${r.order_id}`}
                    className="rounded-full bg-white border border-[#e8dcc8] hover:border-[#4a1c17] text-[#4a1c17] px-3 py-1.5 text-xs font-semibold disabled:opacity-50 inline-flex items-center gap-1"
                  >
                    <Recycle className="h-3.5 w-3.5" />
                    Restituito
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
